import {RefObject, createContext} from 'preact';
import {Signal, signal, useSignal} from '@preact/signals';
import {useEffect, useMemo, useRef} from 'preact/hooks';

import {useProductDetails} from 'utilities/product';
import {useDeliveryEstimate} from 'utilities/delivery-promise';
import {useLocation} from 'utilities/location';
import {trackFormToggle} from 'utilities/tracking';

export interface AppState {
  zipFormVisible: Signal<boolean>;
  mainButtonRef: RefObject<HTMLDivElement>;
}

export function useAppState() {
  const zipFormPrevValue = useRef<boolean | undefined>();
  const mainButtonRef = useRef<HTMLDivElement>(null);
  const zipFormVisible = useSignal(false);
  const {productDetails, variantId} = useProductDetails();
  const {deliveryEstimate} = useDeliveryEstimate();
  const {postalCode} = useLocation();

  useEffect(() => {
    return zipFormVisible.subscribe((value) => {
      if (zipFormPrevValue.current !== undefined) {
        trackFormToggle({
          productId: productDetails?.id,
          variantId: variantId ?? undefined,
          deliveryEstimate,
          isOpen: value,
          postalCodeOverride: postalCode,
        });
      }
      zipFormPrevValue.current = value;
    });
  }, [
    deliveryEstimate,
    postalCode,
    productDetails?.id,
    variantId,
    zipFormVisible,
  ]);

  return useMemo(() => ({zipFormVisible, mainButtonRef}), [zipFormVisible]);
}

export const AppState = createContext<AppState>({
  zipFormVisible: signal(false),
  mainButtonRef: {current: null},
});
